import { CheckCircle, Schedule, Upgrade } from '@mui/icons-material';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import useErrors from '../../../../hooks/useErrors';
import server from '../../../../services/server';
import { getUpgradeApplyReadiness } from '../../../../services/proposalGates';

/**
 * Upgrade: o cliente assina e paga; o admin aplica no clube.
 * Só aparece quando a proposta já tem clubId.
 */
export function ApplyUpgrade({ contract, mutate }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const confirm = useConfirm();
    const [isApplying, setIsApplying] = useState(false);

    if (!contract?.clubId) return null;

    const { alreadyApplied, canApply, missing, skips } = getUpgradeApplyReadiness(contract);
    const skipHints = [
        skips.signature && 'assinatura dispensada',
        skips.plan && 'pagamento do plano dispensado',
        skips.custom && 'pagamento da customização dispensado',
    ].filter(Boolean);

    const handleApply = async () => {
        try {
            await confirm({
                title: 'Aplicar upgrade no clube',
                description:
                    'Isso atualiza o clube com o plano, funções, customizações e benefícios desta proposta. Continuar?',
                confirmationText: 'Aplicar',
            });
            setIsApplying(true);
            const { data } = await server.put(`subscription/${contract.uuid}/apply-upgrade`);
            await mutate();
            enqueueSnackbar(data?.message || 'Upgrade aplicado no clube.');
            } catch (error) {
                setErrors(error);
            } finally {
                setIsApplying(false);
            }
    };

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                O cliente assina e paga na página pública. Você aplica o upgrade no clube quando estiver tudo
                pronto — não acontece automaticamente na assinatura nem no pagamento.
            </Typography>

            {alreadyApplied ? (
                <Alert severity="success">
                    Upgrade já aplicado
                    {contract.contractData?.upgradeAppliedAt
                        ? ` em ${new Date(contract.contractData.upgradeAppliedAt).toLocaleString('pt-BR')}`
                        : ''}
                    .
                </Alert>
            ) : !canApply ? (
                <Alert severity="info">
                    Ainda faltam: {missing.join(', ')}.
                </Alert>
            ) : (
                <Alert severity="warning" icon={<Upgrade fontSize="inherit" />}>
                    Pronto para aplicar
                    {skipHints.length ? ` (${skipHints.join(', ')})` : ': plano, assinatura e customização ok'}
                    .
                </Alert>
            )}

            <Stack direction="row" justifyContent="flex-end">
                <Button
                    variant="contained"
                    disabled={!canApply || isApplying}
                    onClick={handleApply}
                    startIcon={isApplying ? <Schedule /> : <CheckCircle />}
                >
                    {isApplying ? 'Aplicando...' : 'Aplicar upgrade no clube'}
                </Button>
            </Stack>
        </Stack>
    );
}
